import React, { useState } from "react";
import {
	InertiaLink,
	usePage
} from "@inertiajs/inertia-react";
import { Inertia } from "@inertiajs/inertia";
import Modal from "@/components/Modal";
import api from "@/services/api";

/**
 * Content component that provides the main application layout with header and navigation
 * Handles user authentication, product management, and shopping cart functionality
 *
 * @param {Object} props - Component props
 * @param {Object} props.session - Current session data including product count
 * @param {Object} props.product - Current product data
 * @param {string} props.step - Current step in the configuration process
 * @param {Array} props.steps - Array of available navigation steps
 * @param {ReactNode} props.children - Child components to render in content area
 * @param {string} props.shoppingurl - URL for shopping cart/checkout
 * @param {Function} props.clickShopping - Optional validation function for shopping cart access
 * @param {boolean} props.validates - Whether to validate before navigation (default: false)
 * @param {string} props.dashboardurl - Admin dashboard URL (default: "/dashboard/users")
 */
const Content = ({
    session,
    product,
    step,
    steps,
    children,
    shoppingurl,
    clickShopping,
    validates = false,
    dashboardurl = "/dashboard/users",
    isSaving = false,
}) => {
    // Get authenticated user data from Inertia page props
    const { auth } = usePage().props;

    // State for controlling the shopping popup modal
    const [shoppingPopupOpen, setShoppingPopupOpen] = useState(false);

    /**
     * Handle user logout
     * Posts to logout endpoint and redirects to login page
     *
     * @param {Event} e - The click event
     */
    function logout(e) {
        e.preventDefault();
		api.post("/logout").then(() => {
            window.location.href = "/login";
        });
    }

    // Find the "Stuklijst" (parts list) step URL from available steps
    const stuklijsturl =
        steps && steps.length > 0
            ? steps.find((step) => step.name === "Stuklijst").route
            : null;

    /**
     * Check if product has variations before allowing shopping cart access
     * Shows popup if no variations exist, otherwise navigates to shopping cart
     */
    function checkEmptyVariations() {
        if (step !== "shopping" && product && product.variations && product.variations.length <= 0) {
            setShoppingPopupOpen(true); // Show warning popup
        } else {
            Inertia.visit(shoppingurl); // Navigate to shopping cart
        }
    }

    /**
     * Navigate to the variations/parts list page
     */
    function goToVariations() {
        Inertia.visit(stuklijsturl);
    }

    /**
     * Delete the current product via API call
     * Redirects to URL returned by server after successful deletion
     */
    function deleteProduct() {
        api.delete(
            `/api/product/${product.session_id}/${product.product_id}`,
        ).then((response) => {
            window.location.href = response.data.url;
        });
    }

    /**
     * Handle shopping cart button click with optional validation
     * Runs validation function if provided and validates flag is true
     *
     * @param {Event} e - The click event
     */
    function shoppingClick(e) {
        e.preventDefault();
        if (validates && clickShopping) {
            // Run validation before proceeding
            if (clickShopping()) {
                checkEmptyVariations();
            }
        } else {
            // No validation needed, proceed directly
            checkEmptyVariations();
        }
    }

    return (
        <div className="content h-100 flex-grow-1">

            {/* Header bar with user info and navigation */}
            <div className="sidepanellogin d-flex justify-content-between">

                <div className="content__saving-overlay">
                    {isSaving && (
                        <div className="content__saving-text"> 
                            <svg className="content__saving-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                                {/* !Font Awesome Free 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc. */}
                                <path d="M304 48a48 48 0 1 0-96 0 48 48 0 1 0 96 0zm0 416a48 48 0 1 0-96 0 48 48 0 1 0 96 0zM48 304a48 48 0 1 0 0-96 48 48 0 1 0 0 96zm464-48a48 48 0 1 0-96 0 48 48 0 1 0 96 0zM142.9 437A48 48 0 1 0 75 369.1a48 48 0 1 0 67.9 67.9zm0-294.2A48 48 0 1 0 75 75a48 48 0 1 0 67.9 67.9zM369.1 437a48 48 0 1 0 67.9-67.9 48 48 0 1 0-67.9 67.9z"/>
                            </svg>
                            Opslaan...
                        </div>
                    )}
                </div>

                <div className="d-flex align-items-center">

                {/* User profile section */}
                <div className="align-items-center mr-3 d-flex align-self-center">

                    {/* User avatar icon */}
                    <svg
                        className="mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        height="25"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                    {auth.user.name}
                </div>

                {/* Logout button */}
                <a
                    href="#"
                    onClick={logout}
                    name="Uitloggen"
                    className="align-self-center"
                >
                    <svg
                        height="25"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        className="mr-3"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                    </svg>
                </a>

                {/* Admin dashboard link (only visible to admin users) */}
                {auth.user.admin ? (
                    <InertiaLink
                        href={dashboardurl}
                        name="Alle gebruikers"
                        className="d-inline-flex align-self-center"
                    >
                        <svg
                            className="mr-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            height="25"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                            />
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                        </svg>
                    </InertiaLink>
                ) : (
                    <></>
                )}

                {/* Shopping cart button with product count (only shown when session exists) */}
                {session ? (
                    <a
                        href="#"
                        className="step headquestion d-inline-flex align-self-center align-items-center"
                        name={"Winkelmandje"}
                        onClick={shoppingClick}
                    >
                        <svg
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            height="25"
                            className="mr-1"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                            />
                        </svg>
                        {session.products_count}
                    </a>
                    ) : (
                        <></>
                    )}
                </div>
            </div>

            {/* Main content area */}
            <div className="contentborder">
                <div className="row no-gutters">{children}</div>
            </div>

            {/* Modal for handling incomplete products */}
            <Modal
                open={shoppingPopupOpen}
                title={"Product nog niet afgerond"}
                content={
                    "De stuklijst is niet ingevuld voor dit product. Wil je het product verwijderen en naar de winkelwagen gaat of de stuklijst vullen?"
                }
                savetitle="Ga naar stuklijst"
                buttonclass="btn-orange"
                canceltitle="Verwijder product"
                savefunction={goToVariations}
                cancelfunction={deleteProduct}
                updateOpen={setShoppingPopupOpen}
            />
        </div>
    );
};

export default Content;
